// Nov 2026 — Lightweight TTL cache for workspace-scoped lookup
// dropdowns (clients / brands / projects). Multiple components mount
// the same dropdown on every page nav and used to each hit the
// network. This cache memoises the result per workspace for a short
// TTL and dedupes in-flight requests so parallel callers share one
// response.
import api from "./api";

const TTL_MS = 60 * 1000; // 60s — fresh enough for sheet work
const _data = new Map();      // key -> { expiresAt, value }
const _inflight = new Map();  // key -> Promise

/**
 * Fetch a lookup endpoint with per-key TTL caching + in-flight dedupe.
 *
 * @param {string} key    stable cache key (e.g. `clients:${wsId}`)
 * @param {string} path   api.get path (relative to /api/v1)
 * @returns {Promise<Array>}
 */
export async function getLookup(key, path) {
  const now = Date.now();
  const cached = _data.get(key);
  if (cached && cached.expiresAt > now) return cached.value;
  const existing = _inflight.get(key);
  if (existing) return existing;
  const p = (async () => {
    try {
      const { data } = await api.get(path);
      const arr = Array.isArray(data) ? data : [];
      _data.set(key, { expiresAt: Date.now() + TTL_MS, value: arr });
      return arr;
    } catch {
      // On failure, cache an empty array briefly to avoid request
      // storms, but with a much shorter TTL so a recovery is picked up
      // quickly.
      _data.set(key, { expiresAt: Date.now() + 5000, value: [] });
      return [];
    } finally {
      _inflight.delete(key);
    }
  })();
  _inflight.set(key, p);
  return p;
}

/** Convenience wrappers for the three hot dropdowns. */
export const clientsLookup = (wsId) =>
  getLookup(`clients:${wsId}`, `/workspaces/${wsId}/clients/lookup`);
export const brandsLookup = (wsId) =>
  getLookup(`brands:${wsId}`, `/workspaces/${wsId}/brands/lookup`);
export const projectsLookup = (wsId) =>
  getLookup(`projects:${wsId}`, `/workspaces/${wsId}/projects/picker`);

/**
 * Invalidate cached lookups. Call after create/update/delete so the
 * dropdowns pick up the new row on the next render.
 */
export function invalidateLookup(kind, wsId) {
  if (!wsId) return;
  if (kind) {
    _data.delete(`${kind}:${wsId}`);
  } else {
    _data.delete(`clients:${wsId}`);
    _data.delete(`brands:${wsId}`);
    _data.delete(`projects:${wsId}`);
  }
}

export function invalidateAllLookups() {
  _data.clear();
}
